import {
    BadgeCheck,
    CalendarPlus,
    Clock3,
    Dumbbell,
    Mail,
    Pencil,
    Phone,
    Sparkles,
    Star,
    Store,
    Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import type { CoachProfile } from "@flexv-coach/shared";
import { Alert } from "@/components/ui/Alert";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Field, Input, Textarea } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { SportCover } from "@/components/sport/SportMedia";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/providers/AuthProvider";
import { ApiError } from "@/lib/api";
import { employmentLabel } from "@/lib/labels";
import { formatDuration, formatLongDate } from "@/lib/date";
import { payrollApi } from "@/lib/api";
import { useUpdateProfile } from "./useUpdateProfile";

/* -------------------------------------------------------------------------- */
/* Presentational pieces                                                      */
/* -------------------------------------------------------------------------- */

function InfoRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="app-text-soft mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
                <dt className="app-text-soft text-xs font-medium">{label}</dt>
                <dd className="app-text-primary mt-0.5 truncate text-sm font-semibold">{value}</dd>
            </div>
        </div>
    );
}

/** Stat kecil di pita kartu hero — angka besar, label mikro, pembatas hairline. */
function HeroStat({ label, value }: { label: string; value: string }) {
    return (
        <div className="min-w-0 flex-1 text-center">
            <p className="app-text-primary truncate text-xl font-bold tracking-tight sm:text-2xl">
                {value}
            </p>
            <p className="app-text-soft mt-0.5 truncate text-[11px] font-medium uppercase tracking-[0.14em]">
                {label}
            </p>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Edit modal                                                                 */
/* -------------------------------------------------------------------------- */

function EditProfileModal({
    coach,
    open,
    onClose,
    onSaved,
}: {
    coach: CoachProfile;
    open: boolean;
    onClose: () => void;
    onSaved: (message: string) => void;
}) {
    const { t } = useI18n();
    const { refreshProfile } = useAuth();
    const [displayName, setDisplayName] = useState(coach.displayName ?? coach.coachName);
    const [phone, setPhone] = useState(coach.phone ?? "");
    const [bio, setBio] = useState(coach.bio ?? "");
    const [formError, setFormError] = useState<string | null>(null);

    // Sinkron ulang tiap modal dibuka (bukan sekali di mount).
    useEffect(() => {
        if (open) {
            setDisplayName(coach.displayName ?? coach.coachName);
            setPhone(coach.phone ?? "");
            setBio(coach.bio ?? "");
            setFormError(null);
        }
    }, [open, coach]);

    const update = useUpdateProfile(refreshProfile);

    const dirty =
        displayName.trim() !== (coach.displayName ?? coach.coachName) ||
        phone.trim() !== (coach.phone ?? "") ||
        bio.trim() !== (coach.bio ?? "");

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setFormError(null);

        update.mutate(
            {
                displayName: displayName.trim() || undefined,
                phone: phone.trim() || undefined,
                // String kosong → hapus bio (backend menyimpan null).
                bio: bio.trim() === "" ? (coach.bio ? "" : undefined) : bio.trim(),
            },
            {
                onSuccess: () => {
                    onSaved(t("profile.edit.success"));
                    onClose();
                },
                onError: (error: unknown) => {
                    if (error instanceof ApiError && error.status === 409) {
                        setFormError(t("profile.edit.phoneTaken"));
                    } else if (error instanceof ApiError && error.message) {
                        setFormError(error.message);
                    } else {
                        setFormError(t("profile.edit.failed"));
                    }
                },
            },
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={t("profile.edit.title")}
            description={t("profile.edit.subtitle")}
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Field label={t("profile.edit.displayName")} required>
                    <Input
                        value={displayName}
                        onChange={(event) => setDisplayName(event.target.value)}
                        placeholder={t("profile.edit.displayNamePlaceholder")}
                        minLength={2}
                        maxLength={120}
                        required
                    />
                </Field>

                <Field label={t("profile.edit.phone")} required>
                    <Input
                        value={phone}
                        onChange={(event) => setPhone(event.target.value)}
                        placeholder={t("profile.edit.phonePlaceholder")}
                        inputMode="tel"
                        minLength={8}
                        maxLength={24}
                        required
                    />
                </Field>

                <Field label={t("profile.edit.bio")}>
                    <Textarea
                        value={bio}
                        onChange={(event) => setBio(event.target.value)}
                        placeholder={t("profile.edit.bioPlaceholder")}
                        maxLength={500}
                        rows={4}
                    />
                </Field>

                {formError && <Alert tone="error">{formError}</Alert>}

                <div className="flex items-center justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={onClose}>
                        {t("profile.edit.cancel")}
                    </Button>
                    <Button type="submit" loading={update.isPending} disabled={!dirty}>
                        {t("profile.edit.submit")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

type PayrollLite =
    | { state: "loading" }
    | { state: "ready"; sessions: number; minutes: number }
    | { state: "empty" };

/**
 * Profil coach — hero kartu (nested shell, eyebrow status + avatar besar +
 * pita statistik), lalu bento asimetris: outlet & olahraga (lebar) di kanan,
 * kontak & detail akun (sempit) di kiri. Edit profil lewat modal
 * (`PATCH /coach/me`).
 *
 * ponytail: statistik sesi/jam/outlet saat ini dibaca dari konteks profil
 * + payroll bulan berjalan. Bila BE kelak punya endpoint `GET /coach/stats`,
 * ganti `PayrollLite` dengan angka server tanpa mengubah layout.
 */
export function ProfilePage() {
    const { coach } = useAuth();
    const { t, locale } = useI18n();
    const [editorOpen, setEditorOpen] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [payroll, setPayroll] = useState<PayrollLite>({ state: "loading" });

    const month = useMemo(() => new Date().toISOString().slice(0, 7), []);

    useEffect(() => {
        let cancelled = false;
        payrollApi
            .summary(month)
            .then((summary) => {
                if (cancelled) return;
                setPayroll({
                    state: "ready",
                    sessions: summary.eligibleSessions,
                    minutes: summary.eligibleMinutes,
                });
            })
            .catch(() => {
                if (!cancelled) setPayroll({ state: "empty" });
            });
        return () => {
            cancelled = true;
        };
    }, [month]);

    if (!coach) return null;

    const name = coach.displayName ?? coach.coachName;
    const memberSince = formatLongDate(coach.createdAt.slice(0, 10));
    const primaryOutlet = coach.affiliations.find((a) => a.isPrimary) ?? coach.affiliations[0];

    const sessionsValue = payroll.state === "ready" ? String(payroll.sessions) : "—";
    const hoursValue = payroll.state === "ready" ? formatDuration(payroll.minutes) : "—";

    return (
        <div className="flex flex-col gap-6">
            {/* --- HERO: nested shell + eyebrow + avatar + pita stat ---------- */}
            <section className="app-content-subtle rounded-[2rem] p-1.5 sm:p-2">
                <div className="app-content-card relative overflow-hidden rounded-[calc(2rem-0.375rem)]">
                    {/* Wash lembut di kanan atas — satu aksen, opasitas rendah,
                        supaya hero terasa hidup tanpa menabrak gaya monokrom
                        yang sudah disepakati. */}
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[var(--color-primary)] opacity-[0.08] blur-3xl"
                    />

                    <div className="relative flex flex-col gap-6 p-5 sm:p-7">
                        {!coach.emailVerifiedAt && (
                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-4 py-3">
                                <p className="text-sm text-[var(--color-warning-text)]">
                                    {t("profile.unverifiedEmail")}
                                </p>
                                <Link
                                    to="/verify-email"
                                    state={{ email: coach.email ?? "" }}
                                    className="text-sm font-semibold text-[var(--color-primary)]"
                                >
                                    {t("profile.verifyNow")}
                                </Link>
                            </div>
                        )}

                        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
                            <Avatar name={name} src={coach.avatarUrl} size="xl" />
                            <div className="min-w-0 flex-1">
                                <p className="app-content-subtle app-text-soft inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]">
                                    <Sparkles className="h-3 w-3" aria-hidden="true" />
                                    {coach.affiliations.length > 0
                                        ? (primaryOutlet?.outlet?.name ?? t("profile.outlets"))
                                        : t("profile.noAffiliations")}
                                </p>
                                <h2 className="app-text-primary mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
                                    {name}
                                </h2>
                                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                                    <Badge tone="primary">{employmentLabel(coach.employmentType)}</Badge>
                                    {coach.rating !== null && (
                                        <Badge tone="warning">
                                            <Star className="h-3 w-3" />
                                            {coach.rating.toFixed(1)}
                                        </Badge>
                                    )}
                                    {coach.emailVerifiedAt && (
                                        <Badge tone="success">
                                            <BadgeCheck className="h-3 w-3" />
                                            {coach.email ?? ""}
                                        </Badge>
                                    )}
                                </div>
                                {coach.bio && (
                                    <p className="app-shell-text-muted mt-3 max-w-2xl text-sm leading-relaxed">
                                        {coach.bio}
                                    </p>
                                )}
                            </div>
                            <div className="shrink-0">
                                <Button
                                    variant="outline"
                                    icon={<Pencil className="h-4 w-4" />}
                                    onClick={() => setEditorOpen(true)}
                                >
                                    {t("profile.edit.open")}
                                </Button>
                            </div>
                        </div>

                        {/* Pita statistik — pembatas hairline antar angka. */}
                        <dl className="app-shell-border flex items-stretch gap-2 border-t pt-5">
                            <HeroStat
                                label={t("profile.stats.sessions")}
                                value={sessionsValue}
                            />
                            <div aria-hidden="true" className="app-shell-border w-px self-stretch border-l" />
                            <HeroStat label={t("profile.stats.hours")} value={hoursValue} />
                            <div aria-hidden="true" className="app-shell-border w-px self-stretch border-l" />
                            <HeroStat
                                label={t("profile.stats.outlets")}
                                value={String(coach.affiliations.length)}
                            />
                            <div aria-hidden="true" className="app-shell-border w-px self-stretch border-l" />
                            <HeroStat
                                label={t("profile.stats.rating")}
                                value={coach.rating === null ? "—" : coach.rating.toFixed(1)}
                            />
                        </dl>
                    </div>
                </div>
            </section>

            {notice && <Alert tone="success">{notice}</Alert>}

            {/* --- BENTO: kiri sempit (kontak+akun), kanan lebar (outlet+sport)
                Mobile: tumpuk satu kolom, urutan tetap masuk akal. ---------- */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <div className="flex flex-col gap-4 lg:col-span-2">
                    <Card>
                        <CardHeader
                            title={t("profile.contact")}
                            description={t("profile.subtitle")}
                            icon={<Mail className="h-5 w-5" />}
                        />
                        <CardContent className="flex flex-col gap-4">
                            <InfoRow
                                icon={Mail}
                                label={t("profile.email")}
                                value={coach.email ?? "—"}
                            />
                            <InfoRow
                                icon={Phone}
                                label={t("profile.phone")}
                                value={coach.phone ?? "—"}
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader
                            title={t("profile.about")}
                            icon={<Clock3 className="h-5 w-5" />}
                        />
                        <CardContent className="flex flex-col gap-4">
                            <InfoRow
                                icon={CalendarPlus}
                                label={t("profile.joined")}
                                value={memberSince}
                            />
                            <InfoRow
                                icon={Wallet}
                                label={t("profile.stats.outlets")}
                                value={
                                    primaryOutlet?.outlet?.name
                                        ? `${primaryOutlet.outlet.name}${primaryOutlet.isPrimary ? ` · ${t("profile.primary")}` : ""}`
                                        : "—"
                                }
                            />
                            <p className="app-text-soft text-xs">
                                {new Intl.DateTimeFormat(locale, {
                                    dateStyle: "full",
                                    timeZone: "UTC",
                                }).format(new Date(`${coach.createdAt.slice(0, 10)}T00:00:00.000Z`))}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                <div className="flex flex-col gap-4 lg:col-span-3">
                    <Card>
                        <CardHeader
                            title={t("profile.outlets")}
                            description={
                                primaryOutlet?.outlet?.city
                                    ? `${primaryOutlet.outlet.city} · ${coach.affiliations.length}`
                                    : undefined
                            }
                            icon={<Store className="h-5 w-5" />}
                            action={
                                <Link
                                    to="/affiliations"
                                    className="app-text-primary text-sm font-semibold underline"
                                >
                                    {t("nav.affiliations.label")}
                                </Link>
                            }
                        />
                        <CardContent noPadding>
                            {coach.affiliations.length === 0 ? (
                                <p className="app-text-soft px-5 py-6 text-sm">
                                    {t("profile.noAffiliations")}
                                </p>
                            ) : (
                                <ul className="divide-y app-shell-border">
                                    {coach.affiliations.map((affiliation) => (
                                        <li
                                            key={affiliation.id}
                                            className="flex items-center justify-between gap-3 px-5 py-4"
                                        >
                                            <div className="min-w-0">
                                                <p className="app-text-primary truncate text-sm font-semibold">
                                                    {affiliation.outlet?.name ?? "Outlet"}
                                                </p>
                                                <p className="app-text-soft truncate text-xs">
                                                    {affiliation.outlet?.city ?? "—"}
                                                </p>
                                            </div>
                                            {affiliation.isPrimary && (
                                                <Badge tone="primary">{t("profile.primary")}</Badge>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader
                            title={t("profile.sports")}
                            icon={<Dumbbell className="h-5 w-5" />}
                            action={
                                <Link
                                    to="/sports"
                                    className="app-text-primary text-sm font-semibold underline"
                                >
                                    {t("nav.sports.label")}
                                </Link>
                            }
                        />
                        <CardContent noPadding>
                            {coach.sports.length === 0 ? (
                                <p className="app-text-soft px-5 py-6 text-sm">
                                    {t("profile.noAffiliations")}
                                </p>
                            ) : (
                                <ul className="divide-y app-shell-border">
                                    {coach.sports.map((coachSport) => (
                                        <li
                                            key={coachSport.id}
                                            className="flex items-center gap-3 px-5 py-4"
                                        >
                                            {/* Cover asli olahraga: image_url →
                                                icon_url → generik (sama dengan
                                                menu Sport). */}
                                            <SportCover sport={coachSport.sport} />
                                            <div className="min-w-0 flex-1">
                                                <p className="app-text-primary flex items-center gap-2 truncate text-base font-bold tracking-tight">
                                                    <span className="min-w-0 truncate">
                                                        {coachSport.sport?.name}
                                                    </span>
                                                    {coachSport.isPrimary && (
                                                        <Badge tone="primary">
                                                            {t("profile.primary")}
                                                        </Badge>
                                                    )}
                                                </p>
                                                {coachSport.sport?.description && (
                                                    <p className="app-text-soft mt-0.5 truncate text-sm">
                                                        {coachSport.sport.description}
                                                    </p>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            <EditProfileModal
                coach={coach}
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                onSaved={setNotice}
            />
        </div>
    );
}
